import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicLayout from "@/components/layout/PublicLayout";
import { useLang } from "@/i18n";
import { CheckCircle2, AlertCircle } from "lucide-react";

const VerifyEmail = () => {
  const { t, setLangAuto } = useLang();
  const { logout } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState(token ? "loading" : "idle");

  useEffect(() => {
    if (!token) return;
    api.post("/auth/verify-email", { token })
      .then((res) => {
        // MF-3 i18n (May 2026) — apply the user's preferred language
        // returned by the backend so this success view (and the next
        // /login screen, via the localStorage mirror) render in the
        // account's locale instead of the navigator/browser default.
        // setLangAuto is a no-op if the visitor already made a manual
        // language choice in this browser session.
        try {
          const userLang = res?.data?.language;
          if (userLang) setLangAuto(userLang);
        } catch { /* never block success on i18n */ }
        // EVH (Feb 2026) — force re-login. Any soft-login session in this
        // browser is dropped so the user gets a CLEAN full-token session
        // when they sign back in. This is the explicit "Option 1a"
        // approved by José + Lumia: no auto-upgrade to a full session.
        try { logout(); } catch {}
        setStatus("ok");
      })
      .catch(() => setStatus("fail"));
  }, [token, logout, setLangAuto]);

  return (
    <PublicLayout>
      <div className="tsw-section py-20 max-w-lg text-center">
        <h1 className="font-display font-bold text-3xl text-slate-900">{t("auth.verifyTitle")}</h1>
        <div className="mt-8 tsw-card p-8" data-testid="verify-card">
          {status === "idle" && <p className="text-slate-600">{t("auth.verifyBody")}</p>}
          {status === "loading" && <p className="text-slate-600">{t("common.loading")}</p>}
          {status === "ok" && (
            <div className="flex flex-col items-center gap-3">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              <p className="text-emerald-700 font-medium">{t("auth.verifySuccess")}</p>
              <Link to="/login" className="tsw-btn-primary mt-2" data-testid="verify-to-login">{t("auth.submitLogin")}</Link>
            </div>
          )}
          {status === "fail" && (
            <div className="flex flex-col items-center gap-3">
              <AlertCircle className="w-10 h-10 text-rose-500" />
              <p className="text-rose-600 font-medium">{t("auth.verifyFail")}</p>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default VerifyEmail;
